import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Chip, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { PrimaryGrid } from '@ui/components/common/Grid';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { APIStatus } from '@application/enums/api-status';
import { fetchFamilyFestCompanyAttendeesGridAsync } from '@application/features/familyFest/familyFestCompanyAttendeesGridThunk';
const OtherCompanyAttendeesGrid = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { eventId } = useParams();
    const dispatch = useAppDispatch();
    const { items, totalCount, status } = useAppSelector((state) => state.familyFestCompanyAttendeesGrid);
    const { companies } = useAppSelector((state) => state.familyFestCompanyAttendees);
    const selectedCompanyIds = useAppSelector((state) => state.company.selectedCompanyId);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
    const [sortModel, setSortModel] = useState([]);
    const [companyIdFilter, setCompanyIdFilter] = useState();
    const fetch = useCallback(() => {
        if (!eventId)
            return;
        const sort = sortModel[0];
        dispatch(fetchFamilyFestCompanyAttendeesGridAsync({
            eventId,
            request: {
                pageNumber: paginationModel.page + 1,
                pageSize: paginationModel.pageSize,
                companyIdFilter: companyIdFilter || undefined,
                sortBy: sort?.field,
                sortOrder: sort?.sort ?? 'desc'
            }
        }));
    }, [eventId, dispatch, paginationModel, sortModel, companyIdFilter, selectedCompanyIds]);
    const companyFilterOptions = useMemo(() => companies.map((c) => ({ value: c.companyId, label: c.companyName })), [companies]);
    useEffect(() => {
        fetch();
    }, [fetch]);
    const renderTotalChip = (value) => (_jsx(Chip, { label: value, size: 'small', sx: {
            minWidth: 44,
            height: 28,
            borderRadius: '8px',
            bgcolor: theme.palette.text.primary,
            color: theme.palette.common.white,
            fontWeight: 600,
            '& .MuiChip-label': { px: 1.5, fontSize: '0.8125rem' }
        } }));
    const renderCountChip = (value) => (_jsx(Chip, { label: value, size: 'small', variant: 'soft', color: 'default', sx: {
            minWidth: 41,
            height: 32,
            borderRadius: '8px',
            '& .MuiChip-label': { px: 1.5, fontSize: '0.8125rem' }
        } }));
    const columns = useMemo(() => [
        {
            field: 'companyName',
            type: 'string',
            headerName: t('events.eventDetails.familyFestDashboard.company'),
            flex: 1.4,
            minWidth: 200,
            sortable: true,
            renderCell: (params) => (_jsxs(Box, { display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, width: '100%', children: [_jsx(Box, { component: 'img', sx: { objectFit: 'contain' }, src: params.row.logoUrl ?? '', alt: params.row.companyName, width: 32, height: 32, borderRadius: 1, flexShrink: 0 }), _jsx(Typography, { variant: 'body2', noWrap: true, title: params.row.companyName, children: params.row.companyName })] }))
        },
        {
            field: 'totalAttendees',
            headerName: t('events.eventDetails.familyFestDashboard.totalAttendees'),
            flex: 0.8,
            minWidth: 130,
            align: 'center',
            headerAlign: 'center',
            sortable: true,
            renderCell: (params) => renderTotalChip(Number(params.value ?? 0))
        },
        {
            field: 'individuals',
            headerName: t('events.eventDetails.familyFestDashboard.individuals'),
            flex: 0.8,
            minWidth: 120,
            align: 'center',
            headerAlign: 'center',
            sortable: true,
            renderCell: (params) => renderCountChip(Number(params.value ?? 0))
        },
        {
            field: 'familyMembers',
            headerName: t('events.eventDetails.familyFestDashboard.familyMembers'),
            flex: 0.9,
            minWidth: 140,
            align: 'center',
            headerAlign: 'center',
            sortable: true,
            renderCell: (params) => renderCountChip(Number(params.value ?? 0))
        },
        {
            field: 'partners',
            headerName: t('events.eventDetails.familyFestDashboard.partners'),
            flex: 0.7,
            minWidth: 110,
            align: 'center',
            headerAlign: 'center',
            sortable: true,
            renderCell: (params) => renderCountChip(Number(params.value ?? 0))
        }
    ], [t, theme]);
    return (_jsx(PrimaryGrid, { columns: columns, rows: items, getRowId: (row) => row.companyId, loading: status === APIStatus.PENDING, showHeaderControls: true, haveSearch: false, showExportExcelButton: false, showFilters: true, filterOptions: { companyName: companyFilterOptions }, onApplyFilters: (filters) => {
            setCompanyIdFilter(filters['companyName'] || undefined);
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
        }, paginationMode: 'server', rowCount: totalCount, paginationModel: paginationModel, onPaginationModelChange: setPaginationModel, sortingMode: 'server', sortModel: sortModel, onSortModelChange: (model) => {
            setSortModel(model);
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
        }, pageSizeOptions: [5, 10, 25, 50], maxHeight: '520px', noRowsText: t('common.noRecordFound'), showText: true, textLabel: t('events.eventDetails.familyFestDashboard.otherCompanyAttendees'), disableRowSelectionOnClick: true }));
};
export default OtherCompanyAttendeesGrid;
